/**
 * Branch categorization for the Repository page — derives a `Branch['type']` from the
 * branch name by convention, and flags which kinds are transient (machine-generated,
 * hidden by default to keep the list compact).
 *
 * Single source of truth: both the real (AzureDevOps/HTTP) and Mock git services import
 * `deriveBranchType` from here so categorization can't drift between them.
 */
import type { Branch } from '../../types/git.types'

export function deriveBranchType(name: string): Branch['type'] {
  if (name === 'main' || name === 'master') return 'main'
  if (/^org[/-]/i.test(name)) return 'stage'           // pipeline stage/lane branches (org/dev-source, org/sit…)
  if (/^promote[/-]/i.test(name)) return 'promotion'   // transient PR branches the pipeline creates
  if (/^capture[/-]/i.test(name)) return 'capture'     // drift capture-backs
  if (/^(e2e|deploy)[/-]/i.test(name)) return 'system' // e2e / system-generated
  if (/^phase[/-]/i.test(name)) return 'phase'         // legacy
  if (/^feat(ure)?[/-]/i.test(name)) return 'feature'
  if (/^fix[/-]/i.test(name) || /^bug[/-]/i.test(name) || /^hotfix[/-]/i.test(name)) return 'fix'
  return 'other'
}

/** Display label per type (capitalized; used by the filter chips + branch badge). */
export const BRANCH_TYPE_LABEL: Record<Branch['type'], string> = {
  main: 'Main', stage: 'Stage', feature: 'Feature', fix: 'Fix',
  capture: 'Capture', promotion: 'Promotion', system: 'System', phase: 'Phase', other: 'Other',
}

/** Machine-generated / throwaway kinds — hidden by default on the Repository page. */
export const TRANSIENT_BRANCH_TYPES: ReadonlySet<Branch['type']> = new Set<Branch['type']>(['promotion', 'system'])
